import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, tap, switchMap } from "rxjs";
import { UserAdminService } from "./user-admin.service";
import { UpdateUserAvailableDto } from "@Models";
import { FeatureManagerService } from "@Services";
import { ConfirmationDialogService } from './../services/utility/confirmation-dialog.service';
import { MrsColor, FeatureToggle, ConfirmationDialogMessageMode } from 'app/enums';
import { DatePipe } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class UserAvailabilityService {
    private availabilityMap = new Map<string, BehaviorSubject<boolean>>();
    userConfirmed: any = false;
    currentScheduledAvailability: any;
    unavailabilityScheduleIsEnabled: boolean = false;

    constructor(private userAdminService: UserAdminService,
        private readonly confirmationService: ConfirmationDialogService,
        private featureManagerService: FeatureManagerService,
        private datePipe: DatePipe
    ) { }

    subscribeToFeatureToggles() {
        this.featureManagerService.isEnabled(FeatureToggle.GlobalEnableUnavailabilitySchedule).subscribe((result) => {
          if (result) {
            this.unavailabilityScheduleIsEnabled = true;
          }
        });
    }

    loadInitialAvailability(userId: string): void {
        this.subscribeToFeatureToggles();
        this.userAdminService.getUserAvailability(userId).subscribe(response => {
            if (response?.isAvailable !== undefined) {
                this.availabilityMap
                    .get(userId)
                    ?.next(response.isAvailable);
            }
        });
    }

    getAvailability(userId: string): Observable<boolean> {
        if (!this.availabilityMap.has(userId)) {
            this.availabilityMap.set(userId, new BehaviorSubject<boolean>(false));
            this.loadInitialAvailability(userId);
        }
        return this.availabilityMap.get(userId)!.asObservable();
    }

    getCurrentAvailabilityByUser(userId: string): boolean | undefined {
        return this.availabilityMap.get(userId)?.getValue();
    }

    getActiveCurrentUserUnavailabilitySchedule(userId: string) {
        return this.userAdminService.getActiveCurrentUserUnavailabilitySchedule(userId).pipe(
            tap(response => {
                if (response) {
                    this.currentScheduledAvailability = response;
                }
            })
        );
    }

    discardCurrentUserUnavailabilitySchedule(userId: string) {
        const payload = this.currentScheduledAvailability.map((item: any) => {
            item.isActive = false;
            return item;
        });

        if (payload.length > 0) {
            this.userAdminService.upsertUserUnavailabilitySchedule(userId, payload).subscribe((response) => {
            });
        }
    }

    updateAvailability(userId: string, isAvailable: boolean, hideWaiting: boolean = false): Observable<boolean> {
        return this.getActiveCurrentUserUnavailabilitySchedule(userId).pipe(
            switchMap(() => {
                const hasNoSchedule = !this.currentScheduledAvailability || this.currentScheduledAvailability.length === 0;

                if (!this.unavailabilityScheduleIsEnabled || !isAvailable || hasNoSchedule) {
                    const request: UpdateUserAvailableDto = { userId, isAvailable };
                    return this.userAdminService.updateUserAvailability(request, hideWaiting).pipe(
                        tap(response => {
                            if (response === true) {
                                if (!this.availabilityMap.has(userId)) {
                                    this.availabilityMap.set(userId, new BehaviorSubject<boolean>(isAvailable));
                                } else {
                                    this.availabilityMap.get(userId)!.next(isAvailable);
                                }
                            }
                        })
                    );
                }
                let multipleEvents = this.currentScheduledAvailability && this.currentScheduledAvailability.length > 1;
                return new Observable<boolean>((observer) => {
                    this.confirmationService.open({
                        title: multipleEvents? 'Delete User Scheduled Events?' :'Delete User Scheduled Event?',
                        message: this.prepareMessage(multipleEvents),
                        okLabel: multipleEvents? 'YES, DELETE EVENTS' : 'YES, DELETE EVENT',
                        cancelLabel: 'NO, STAY UNAVAILABLE',
                        okTextColor: MrsColor.UIRed,
                        cancelTextColor: MrsColor.UIBlue,
                        messageMode: ConfirmationDialogMessageMode.Html,
                        onOk: () => {
                            const request: UpdateUserAvailableDto = { userId, isAvailable };
                            this.userAdminService.updateUserAvailability(request, hideWaiting).subscribe((response) => {
                                if (response) {
                                    if (!this.availabilityMap.has(userId)) {
                                        this.availabilityMap.set(userId, new BehaviorSubject<boolean>(isAvailable));
                                    } else {
                                        this.availabilityMap.get(userId)!.next(isAvailable);
                                    }
                                    this.discardCurrentUserUnavailabilitySchedule(userId);
                                    observer.next(true);
                                } else {
                                    observer.next(false);
                                }
                                observer.complete();
                            });
                        },
                        showCancel: true,
                        onCancel: () => {
                            observer.next(false);
                            observer.complete();
                        },
                    });
                });
            })
        );
    }

    prepareMessage(multipleEvents: boolean): string {
        let message = 'The following scheduled event will be deleted:<ul>';
        if (this.currentScheduledAvailability) {
            this.currentScheduledAvailability.forEach((item: any) => {
                const start = this.datePipe.transform(item?.formattedStartTime, 'MMMM d, y, h:mm a');
                const end = this.datePipe.transform(item?.formattedEndTime, 'MMMM d, y, h:mm a');
                message += `<li><strong>${start} - ${end}</strong></li>`;
            });
        }
        message += '</ul><div>Are you sure you want to discard the user\'s scheduled event?</div>';

        if (multipleEvents) {
            message = message.replace(/event/g, 'events');
        }

        return message;
    }
}
